/**
 * PanelHighlightService — 지도 위 하이라이트 overlay 피처 관리
 * 
 * SOLID:
 *  - SRP: 하이라이트 overlay 생성/제거만 담당
 *  - DIP: OL 객체(Fill/Stroke/Text/Feature 등)를 생성자에서 주입
 *  - ISP: clear / highlightRegion / highlightSingle 의 간단한 인터페이스
 * 
 * Facade: service.highlightRegion(features, itemStyle, labelText) 만 호출하면
 *         내부에서 OL Style/Feature 생성, 소스 추가를 처리
 */

/**
 * @param {object} deps
 * @param {import('ol/style/Style').default} deps.Style
 * @param {import('ol/style/Stroke').default} deps.Stroke
 * @param {import('ol/style/Fill').default} deps.Fill
 * @param {import('ol/style/Text').default} deps.Text
 * @param {import('ol/Feature').default} deps.Feature
 * @param {import('ol/source/Vector').default} deps.VectorSource
 */
export function createPanelHighlightService(deps) {
  const { Style, Stroke, Fill, Feature } = deps;
  /** @type {import('ol/source/Vector').default|null} */
  let source = null;
  /** @type {import('ol/Feature').default|import('ol/Feature').default[]|null} */
  let currentHighlight = null;

  function setSource(vectorSource) {
    source = vectorSource;
  }

  function clear() {
    if (!source || !currentHighlight) return;
    const list = Array.isArray(currentHighlight)
      ? currentHighlight
      : [currentHighlight];
    list.forEach(f => source.removeFeature(f));
    currentHighlight = null;
  }

  /**
   * @param {import('ol/Feature').default[]} features — 하이라이트할 원본 피처들
   * @param {{ stroke: string, fill: string, width: number }} styleConfig
   * @param {string|null} [labelText]
   * @param {object} [opts]
   * @param {function} [opts.getCentroidFn] — String centroid 계산 함수
   * @returns {void}
   */
  function highlightRegion(features, styleConfig, labelText, opts = {}) {
    clear();
    if (!source || !features?.length) return;

    const highlightFeatures = [];

    for (const f of features) {
      const geom = f.getGeometry();
      if (!geom) continue;

      const polyStyle = new Style({
        stroke: new Stroke({ color: styleConfig.stroke, width: styleConfig.width }),
        fill: new Fill({ color: styleConfig.fill })
      });

      const polyFeature = new Feature({ geometry: geom.clone() });
      polyFeature.setStyle(polyStyle);
      polyFeature.set('_isSelectionHighlight', true);
      highlightFeatures.push(polyFeature);
    }

    if (!highlightFeatures.length) return;

    highlightFeatures.forEach(f => source.addFeature(f));
    currentHighlight = highlightFeatures.length === 1 ? highlightFeatures[0] : highlightFeatures;
  }

  /**
   * @param {import('ol/geom/Geometry').default} geom
   * @param {string} name
   */
  function highlightSingleGeometry(geom, name) {
    clear();
    if (!source || !geom) return;

    const labelStyle = new Style({
      stroke: new Stroke({ color: '#1E88E5', width: 4 }),
      fill: new Fill({ color: 'rgba(30,136,229,0.06)' }),
      ...(name ? {
        text: new deps.Text({
          text: name,
          font: 'bold 14px sans-serif',
          fill: new Fill({ color: '#1565C0' }),
          stroke: new Stroke({ color: '#fff', width: 4 })
        })
      } : {})
    });

    const feat = new Feature({ geometry: geom });
    feat.setStyle(labelStyle);
    feat.set('_isSelectionHighlight', true);
    source.addFeature(feat);
    currentHighlight = feat;
  }

  return { setSource, clear, highlightRegion, highlightSingleGeometry };
}
